import { Controller } from '@hotwired/stimulus'
import { injectHighlight, isBuilderEditingMode, collectGarbage } from '../helpers/utils'
import FlowController from './flow_controller'
import FlowEditController from './flow_edit_controller'

import Auth from '../helpers/auth'
import { env } from '../helpers/env'

import type {
  Flow,
  Component,
  FlowPage,
  HistoryCommandMetadata,
  SetEmbeddablePropertyParams,
  AddEmbeddablePropertyParams,
  RemoveEmbeddablePropertyParams,
  MoveEmbeddablePropertyParams,
  SetMultipleEmbeddableUpdatesParams,
} from '../api/models/types'

const ENV = env()

export default class extends Controller {
  declare flowSelectionCallbacks: ((flowId: string | null, flow: Flow | null) => void)[]
  declare selectedFlowIdValue: string | null
  declare selectedFlowJson: Flow | null
  declare auth: Auth

  declare copiedComponentJson: Component | FlowPage | null
  declare copiedComponentDescendantsJsons: Component[] | null

  static values = {
    selectedFlowId: String,
  }

  async connect() {
    if (!this.flowSelectionCallbacks) this.flowSelectionCallbacks = []

    if (ENV !== 'local' && ENV !== 'ci' && !isBuilderEditingMode()) injectHighlight()

    this.setupWindowFunctions()

    this.auth = new Auth()

    //* If no flow is selected, I select the first flow on the page
    if (!this.selectedFlowIdValue) {
      const savvyEl = document.querySelectorAll('savvy')[0]
      if (savvyEl) {
        const flowId = savvyEl.getAttribute('id')?.replace('flow-', '')
        if (flowId) this.selectedFlowIdValue = flowId
      }
    }
  }

  /**
   * We're just moving the definitions of the window functions to a separate method
   * so that we can call it again if we need to re-initialize the window functions
   */
  setupWindowFunctions(forced?: boolean) {
    if (!window.Savvy || forced) window.Savvy = {}
    if (!window.Embeddables || forced) window.Embeddables = {}

    window.Savvy.getFlowJson = window.Embeddables.getEmbeddableJson = (flowIdOrElement) => {
      const flowEditController = this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlowIdValue)
      return flowEditController
        ? flowEditController.generateCurrentJson()
        : flowController
        ? flowController.getFlow()
        : null
    }

    window.Savvy.setFlowJson = window.Embeddables.setEmbeddableJson = (
      flowIdOrElement,
      json,
      metadata: HistoryCommandMetadata
    ) => {
      return this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )?.updateEntireFlow(json, metadata)
    }

    window.Savvy.setFlowProperty = window.Embeddables.setEmbeddableProperty = (
      flowIdOrElement,
      path,
      value,
      metadata: HistoryCommandMetadata
    ) => {
      this.getFlowEditController(flowIdOrElement || this.selectedFlowIdValue)?.updateFlowProperty(
        path,
        value,
        metadata
      )
    }

    window.Embeddables.setEmbeddablePropertyV2 = ({
      embeddableId,
      path,
      value,
      objectMatchKey,
      alternativePathObjectMatchKeys,
      metadata,
    }: SetEmbeddablePropertyParams) => {
      return this.getFlowEditController(
        embeddableId || this.selectedFlowIdValue
      )?.updateEmbeddableProperty({
        path,
        value,
        metadata,
        objectMatchKey,
        alternativePathObjectMatchKeys,
      })
    }

    window.Savvy.addFlowProperty = window.Embeddables.addEmbeddableProperty = (
      flowIdOrElement,
      arrayPath,
      value,
      position?,
      metadata?: HistoryCommandMetadata
    ) => {
      if (!metadata) return //* Can't mark it as required because is after position which is optional and changing that would require a lot of changes
      return this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )?.addFlowProperty(arrayPath, value, metadata, position)
    }

    window.Embeddables.addEmbeddablePropertyV2 = ({
      embeddableId,
      path,
      value,
      position,
      objectMatchKey,
      alternativePathObjectMatchKeys,
      metadata,
    }: AddEmbeddablePropertyParams) => {
      return this.getFlowEditController(
        embeddableId || this.selectedFlowIdValue
      )?.addEmbeddableProperty({
        path,
        value,
        position,
        objectMatchKey,
        alternativePathObjectMatchKeys,
        metadata,
      })
    }

    window.Savvy.removeFlowProperty = window.Embeddables.removeEmbeddableProperty = (
      flowIdOrElement,
      arrayPath,
      position?,
      metadata?: HistoryCommandMetadata
    ) => {
      if (!metadata) return //* Can't mark it as required because is after position which is optional and changing that would require a lot of changes
      return this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )?.removeFlowProperty(arrayPath, metadata, position)
    }

    window.Embeddables.removeEmbeddablePropertyV2 = ({
      embeddableId,
      path,
      propertyToRemove,
      objectMatchKey,
      isStringMatch,
      alternativePathObjectMatchKeys,
      metadata,
    }: RemoveEmbeddablePropertyParams) => {
      return this.getFlowEditController(
        embeddableId || this.selectedFlowIdValue
      )?.removeEmbeddableProperty({
        path,
        propertyToRemove,
        objectMatchKey,
        isStringMatch,
        alternativePathObjectMatchKeys,
        metadata,
      })
    }

    window.Embeddables.moveEmbeddableProperty = ({
      embeddableId,
      elementId,
      originPath,
      destinationPath,
      position,
      metadata,
    }: MoveEmbeddablePropertyParams) => {
      return this.getFlowEditController(
        embeddableId || this.selectedFlowIdValue
      )?.moveEmbeddableProperty({
        elementId,
        originPath,
        destinationPath,
        position,
        metadata,
      })
    }

    window.Embeddables.setMultipleEmbeddableUpdates = ({
      embeddableId,
      commands,
      metadata,
    }: SetMultipleEmbeddableUpdatesParams) => {
      return this.getFlowEditController(
        embeddableId || this.selectedFlowIdValue
      )?.setMultipleFlowUpdates({ commands, metadata })
    }

    window.Savvy.watchFlowSelection = (callback) => {
      this.flowSelectionCallbacks.push(callback)
      return () => {
        this.flowSelectionCallbacks = this.flowSelectionCallbacks.filter((cb) => cb !== callback)
      }
    }

    window.Savvy.watchEditHistory = window.Embeddables.watchEditHistory = (
      flowIdOrElement: string | Element | null,
      callback
    ) => {
      let flowId: string | null = null
      if (typeof flowIdOrElement === 'string') flowId = flowIdOrElement
      else flowId = flowIdOrElement?.getAttribute('id')?.replace('flow-', '') || null
      if (!flowId) {
        console.error('You must specify a flowId or pass a Flow element')
        return undefined
      } else
        return this.getFlowEditController(flowIdOrElement)?.watchEditHistory(
          (historyIndex, history, flow) => callback(flowId, historyIndex, history, flow)
        )
    }

    window.Savvy.getEditHistory = window.Embeddables.getEditHistory = (flowIdOrElement) => {
      return (
        this.getFlowEditController(flowIdOrElement || this.selectedFlowIdValue)?.getEditHistory() ||
        null
      )
    }

    window.Savvy.clearEditHistory = window.Embeddables.clearEditHistory = (flowIdOrElement) => {
      return this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )?.clearEditHistory()
    }

    window.Embeddables.setEditHistory = (flowIdOrElement, history, historyIndex) => {
      return this.getFlowEditController(
        flowIdOrElement || this.selectedFlowIdValue
      )?.setEditHistory(history, historyIndex)
    }

    window.Savvy.undo = window.Embeddables.undo = (flowIdOrElement) => {
      return this.getFlowEditController(flowIdOrElement || this.selectedFlowIdValue)?.undo() || null
    }

    window.Savvy.redo = window.Embeddables.redo = (flowIdOrElement) => {
      return this.getFlowEditController(flowIdOrElement || this.selectedFlowIdValue)?.redo() || null
    }

    window.Savvy.collectGarbage = () => {
      collectGarbage()
    }

    //* Trigger event - global edit Embeddables functions available
    const globalEditEmbeddablesFunctionsAvailable = new Event(
      'embeddables:global_edit_functions_available'
    )
    window.dispatchEvent(globalEditEmbeddablesFunctionsAvailable)
  }

  selectedFlowIdValueChanged() {
    if (this.selectedFlowIdValue) {
      // remove flow_ from the id
      const flowController = this.getFlowController(this.selectedFlowIdValue)
      this.selectedFlowJson = flowController?.getFlow()
    } else {
      this.selectedFlowJson = null
    }

    if (!this.flowSelectionCallbacks) this.flowSelectionCallbacks = []
    this.flowSelectionCallbacks.forEach((cb) => cb(this.selectedFlowIdValue, this.selectedFlowJson))
    window.dispatchEvent(
      new CustomEvent('savvy:flow_selection_updated', {
        detail: { flow_id: this.selectedFlowIdValue, flow: this.selectedFlowJson },
      })
    )
  }

  getFlowController(flowIdOrElement: string | Element | null): FlowController | null {
    if (!flowIdOrElement) {
      console.error(`You must specify a Flow ID or pass a Flow element`)
    }

    if (typeof flowIdOrElement === 'string') {
      flowIdOrElement = document.querySelector(
        `[data-controller*="flow"][data-flow-id-value="${flowIdOrElement}"]`
      )
    }

    const controller =
      (flowIdOrElement &&
        (this.application.getControllerForElementAndIdentifier(
          flowIdOrElement,
          'flow'
        ) as FlowController | null)) ||
      null

    if (!controller) {
      console.error(`No Flow found "${flowIdOrElement}"`)
    }

    return controller
  }

  getFlowEditController(flowIdOrElement: string | Element | null): FlowEditController | null {
    if (!flowIdOrElement) {
      console.error(`You must specify a Flow ID or pass a Flow element`)
    }

    if (typeof flowIdOrElement === 'string') {
      flowIdOrElement = document.querySelector(
        `[data-controller*="flow-edit"][data-flow-id-value="${flowIdOrElement}"]`
      )
    }

    const controller =
      (flowIdOrElement &&
        (this.application.getControllerForElementAndIdentifier(
          flowIdOrElement,
          'flow-edit'
        ) as FlowEditController | null)) ||
      null

    if (!controller) {
      console.error(`No Flow in Editing Mode found with the ID: "${flowIdOrElement}"`)
    }

    return controller
  }
}
